import {Color} from '@benchling/nucleus-ui';
import {createStyles, Stack} from '@mantine/core';
import React from 'react';

import {Header2} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface EmptyStateCardProps {
  /** Short title shown beneath the icon, e.g. "No configurations". */
  title: string;
  /** Supporting copy beneath the title. May be a string or rich content. */
  message: React.ReactNode;
}

const useStyles = createStyles((theme) => ({
  wrapper: {
    color: Color.GRAY_500,
    width: '100%',
    minHeight: '460px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '24px',
  },
  content: {
    maxWidth: '320px',
    fontSize: theme.fontSizes.md,
    lineHeight: '20px',
  },
  title: {
    fontSize: '18px',
    color: Color.GRAY_500,
    margin: 0,
  },
  icon: {
    color: Color.GRAY_500,
    fontSize: '30px',
  },
}));

/**
 * Centered empty-state card for Benchling app pages (e.g. Configuration, Activity).
 *
 * Renders a gray info icon, a title, and a supporting message. Designed to be placed
 * inside an existing card body (after a header bar) to indicate that there is no data
 * to display yet.
 */
export default function EmptyStateCard({title, message}: EmptyStateCardProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.wrapper}>
      <Stack align="center" spacing="sm" className={classes.content}>
        <BenchlingIcon icon="info-circle" className={classes.icon} />
        <Header2 className={classes.title}>{title}</Header2>
        <div>{message}</div>
      </Stack>
    </div>
  );
}
